import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { api } from '@/lib/clinicApi';

const Context = createContext(null);
export const useAuth = () => useContext(Context);
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const generation = useRef(0);
  const initialRequest = useRef(null);
  useEffect(() => {
    const controller = new AbortController();
    initialRequest.current = controller;
    const version = ++generation.current;
    api.get('/auth/me', { signal: controller.signal, silentAuth: true })
      .then(r => { if (!controller.signal.aborted && generation.current === version) setUser(r.data); })
      .catch(() => { if (!controller.signal.aborted && generation.current === version) setUser(false); });
    const expire = () => { ++generation.current; setUser(false); };
    window.addEventListener('clinic-session-expired', expire);
    return () => { controller.abort(); window.removeEventListener('clinic-session-expired', expire); };
  }, []);
  const login = async (data) => {
    initialRequest.current?.abort();
    const version = ++generation.current;
    await api.post('/auth/login', { ...data, email: data.email.trim().toLowerCase() }, { skipAuthRefresh: true });
    try {
      const verified = await api.get('/auth/me', { skipAuthRefresh: true });
      if (generation.current === version) setUser(verified.data);
      return verified.data;
    } catch (error) {
      if (generation.current === version) setUser(false);
      if (error.response?.status === 401) {
        throw new Error('E-posta ve şifre doğrulandı ancak tarayıcı oturumu kurulamadı. Lütfen siteyi kendi tarayıcı sekmesinde açarak yeniden deneyin.');
      }
      throw error;
    }
  };
  const logout = async () => { await api.post('/auth/logout'); ++generation.current; initialRequest.current?.abort(); setUser(false); };
  return <Context.Provider value={{ user, setUser, login, logout }}>{children}</Context.Provider>;
};
export const ProtectedRoute = () => {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null) return <div className="clinic-loading" data-testid="session-loading">Çalışma alanı hazırlanıyor<span /></div>;
  if (!user) return <Navigate to="/giris" state={{ from: location.pathname }} replace />;
  return <Outlet />;
};